import React from 'react';
import { HeaderThElement, HeaderTdElement } from '@progress/kendo-react-data-tools';
import { TextBox } from '@progress/kendo-react-inputs';
import { plusIcon, minusIcon, filterClearIcon } from '@progress/kendo-svg-icons';
import { formatKeyToTitle } from '.';
import { Button } from '../../Button';
import { Tooltip } from '../../Tooltip';
import { MultiSelect } from '../../Dropdowns/MultiSelect';
import dateTimeUtils from '../../../../../utils/dateTimeUtils';


/**
* Renders a universal header cell for Kendo Grid component.
* 
* If a custom header cell component is provided in `headerCells` for the column,
* it renders that component with merged props. Otherwise, it falls back to a default header cell.
*
* @param {Object} props - Props passed by the Kendo Grid.
* @param {string} props.field - The field name for the column.
* @param {string} props.title - The column title.
* @param {Object} [props.thProps] - Additional props for the header cell element.
* @returns {JSX.Element} The rendered header cell.
*/
const UniversalHeaderCell = (props, columnConfig, headerCells = []) => {
  const { field, title, thProps } = props;
  // finds index of column in columnConfig
  const colIndex = columnConfig.findIndex(col => col.field === field);
  if (
    Array.isArray(headerCells) &&
    headerCells[colIndex] &&
    headerCells[colIndex].component
  ) {
    const HeaderComponent = headerCells[colIndex].component;
    // merges Kendo UI props with custom props, custom props taking precedence
    return (
      <HeaderThElement
        columnId={thProps?.columnId || ''}
        {...thProps}
      >
        <HeaderComponent
          {...headerCells[colIndex].props}
          title={title}
          field={field}
        />
      </HeaderThElement>
    );
  }
  // fallback to default
  return (
    <HeaderThElement
      columnId={thProps.columnId || ''}
      {...thProps}
    >
      {props.children ?? title}
    </HeaderThElement>
  );
};

/**
 * RowExpansionCellElement
 * 
 * @param {Object} props
 * @param {Object} props.headerProps - Kendo header cell props
 * @param {boolean} props.hasExpandedRows - Are any rows expanded?
 * @param {Function} props.onExpandAll - Handler for expand/collapse all
 * @returns {JSX.Element}
 */
const RowExpansionCellElement = ({ headerProps, hasExpandedRows, onExpandAll }) => (
  <HeaderThElement
    {...headerProps} 
    columnId={headerProps.thProps?.columnId || ''}
  >
    {headerProps.field ? (
      headerProps.children
    ) : (
      <div style={{ filter: 'invert(1)' }}>
        <Button
          onClick={onExpandAll}
          fillMode="clear"
          svgIcon={hasExpandedRows ? minusIcon : plusIcon}
        />
      </div>
    )}
  </HeaderThElement>
);

/**
 * Renders cell based on type of data it contains. 
 * Works with headerCell prop in Table component. (deprecated)
 * @param {object} param0 - parameters for function
 * @param {string | React.Fragment | ReactElement} param0.cell - cell data (string, function, or React element) at a given index from a cell array
 * @returns {React.Element} - React element to render
 * @description This function checks the type of data in the cell and renders it accordingly.
 */
const handleRenderCell = ({ cell, ...props }) => {
  if (typeof cell === 'function') {
    // render React component (function/class)
    return <cell {...props} />;
  }
  if (typeof cell === 'string') {
    // render string
    return <span>{cell}</span>;
  }
  if (cell && typeof cell === 'object' && cell.$$typeof) {
    // render React element or React fragment
    if (
      cell.type === React.Fragment &&
      (!cell.props || !cell.props.children)
    ) {
      // empty fragment, so render fallback
      return <span>{props.title || formatKeyToTitle(props.field)}</span>;
    }
    return cell;
  }
  // default fallback
  return <span>{props.title || formatKeyToTitle(props.field)}</span>;
}

/**
 * DefaultTextInputFilterCell
 * This is a custom filter cell made without the filter operator button that comes standard.
 * This was introduced due to a request from Ops to remove the filter operators option.
 * @param {Object} props - properties passed from the grid
 * @param {Function} props.onChange - function to call when input changes
 * @param {Function} props.onClearButtonClick - function to call when clear button is clicked
 * @param {Object} props.thProps - additional props for the header cell element
 * @returns {JSX.Element} - rendered filter cell
 */
const DefaultTextInputFilterCell = props => {
  const handleChange = event => {
    props.onChange({
      value: event.target.value,
      operator: event.target.value ? 'contains' : '',
      syntheticEvent: event
    });
  };
  const handleClearButtonClick = event => {
    event.preventDefault();
    props.onChange({
      value: '',
      operator: '',
      syntheticEvent: event
    });
  };

  const placeholderText = `Filter by ${props.field ? formatKeyToTitle(props.field) : ''}...`;
  return (
    <HeaderTdElement columnId={props.thProps?.columnId || ''} {...props.thProps}>
      <TextBox
        type="text"
        value={props.value || ''}
        onChange={handleChange}
        className="k-textbox"
        placeholder={placeholderText}
        suffix={() =>
          <Button
            disabled={!props.value}
            onClick={handleClearButtonClick}
            svgIcon={filterClearIcon}
          />
      }
      />
    </HeaderTdElement>
  );
};

/**
 * EmployeeProfileLinkCell
 * Custom cell component to render an employee profile link with tooltip and accessibility features.
 * @param {Object} params - Component props
 * @param {string} params.linkLabel - Link label
 * @param {Object} params.dataItem - Data item containing employee information
 * @param {Function} params.onOpenEmployeeDetail - Callback function to open employee detail view
 * @returns {JSX.Element} Rendered EmployeeProfileLink component
 */
const EmployeeProfileLinkCell = ({
  linkLabel,
  dataItem,
  onOpenEmployeeDetail,
  allowViewEmployee
}) => (
  <td>
    {allowViewEmployee && onOpenEmployeeDetail ? (
      <Tooltip
        position="top"
        anchorElement="target"
        content="View Employee Profile"
        id={`employee-profile-tooltip-${dataItem.employeeId}`}
      >
        <a
          href="#"
          className="btn-link p-0"
          onClick={e => {
            e.preventDefault();
            onOpenEmployeeDetail?.(dataItem.employeeId);
          }}
          aria-label={`View profile for ${dataItem.firstName} ${dataItem.lastName}`}
          aria-describedby={`employee-profile-tooltip-${dataItem.employeeId}`}
          title={linkLabel}
          tabIndex={0}
          style={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          {linkLabel}
        </a>
      </Tooltip>
    ) : (
      linkLabel
    )}
  </td>
);

const _getMultiSelectOptions = (rows, field) => {
  const values = field && Array.isArray(rows) && typeof rows[0] === 'object'
    ? rows.map(row => row[field])
    : rows;
  return Array.from(new Set(values)).filter(Boolean);
};

const DropdownMultiSelectFilterCell = ({
  data = [],
  value = [],
  onChange,
  placeholder = 'Select One or More...',
  thProps,
  field
}) => {
  const safeValue = Array.isArray(value) ? value : [];

  const uniqueValues = _getMultiSelectOptions(data, field);

  const handleChange = (e) => {
    const values = e.value;

    onChange({
      value: values,
      operator: (cellValue, filterValues) =>
        !filterValues || filterValues.length === 0 || filterValues.length === uniqueValues.length
          ? true
          : Array.isArray(filterValues) && filterValues.includes(cellValue),
      syntheticEvent: e
    });
  };

  return (
    <HeaderTdElement columnId={thProps?.columnId || ''} {...thProps}>
      <MultiSelect
        data={uniqueValues}
        value={safeValue}
        onChange={handleChange}
        placeholder={placeholder}
        autoClose={false}
        tags={safeValue.length > 0
          ? [{ text: `${safeValue.length} Set`, data: [...safeValue] }]
          : []
        }
      />
    </HeaderTdElement>
  );
};

/**
 * Clear button rendered as the suffix of a filter TextBox. Defined outside the filter
 * cell components so it isn't re-declared on every render (see SonarCloud S6478).
 */
const FilterClearButton = ({ disabled, onClick }) => (
  <Button
    disabled={disabled}
    onClick={onClick}
    svgIcon={filterClearIcon}
  />
);

/**
 * Filter cell for date columns with display-value filtering. Filters against the formatted
 * date string (e.g. 'M/D/YYYY') shown to the user, not the raw ISO value, so typing "8/"
 * will correctly match "8/6/2026" orders.
 */
const FormattedDateFilterCell = ({
  value,
  onChange,
  thProps,
  format = 'M/D/YYYY',
  placeholder = 'Filter by date...'
}) => {
  const handleChange = (event) => {
    const inputValue = event.target.value;
    onChange({
      value: inputValue,
      operator: inputValue
        ? (cellValue) => !!cellValue && dateTimeUtils.dateFormatter.formatLocal(cellValue, format).toLowerCase().includes(inputValue.toLowerCase())
        : ''
    });
  };

  const handleClearButtonClick = (event) => {
    event.preventDefault();
    onChange({ value: '', operator: '' });
  };

  return (
    <HeaderTdElement columnId={thProps?.columnId || ''} {...thProps}>
      <TextBox
        type="text"
        value={value || ''}
        onChange={handleChange}
        className="k-textbox"
        placeholder={placeholder}
        suffix={() => (
          <FilterClearButton
            disabled={!value}
            onClick={handleClearButtonClick}
          />
        )}
      />
    </HeaderTdElement>
  );
};

export {
  UniversalHeaderCell,
  RowExpansionCellElement,
  handleRenderCell,
  DefaultTextInputFilterCell,
  EmployeeProfileLinkCell,
  DropdownMultiSelectFilterCell,
  FormattedDateFilterCell
};